<template>
  <li
    :class="[
      itemId,
      getObjectType(route),
      {
        'is-collapse': itemId === 'Collapse',
        selected:
          selected ||
          isRoute ||
          isPageSelected() ||
          isTaskSelected() ||
          isEventSelected() ||
          isPersonSelected() ||
          isObjectSelected() ||
          isOrganizationSelected() ||
          isWorkspaceSelected() ||
          isDatabaseSelected() ||
          isAgentSelected(),
        isMenuExpanded,
        isDarkMode: theme === 'dark',
      },
    ]"
    class="menu-item"
    role="button"
    tabindex="0"
    @click="onClick"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <a
      ref="itemEl"
      class="menu-name"
      :class="{ icon: !isMenuExpanded }"
      :aria-current="isRoute ? 'page' : undefined"
      draggable="false"
      @dragstart.prevent
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
    >
      <div
        v-if="isBadgeDisplayed && (!counter || !isMenuExpanded)"
        class="badge"
        :class="{ counter, urgent: isBadgeUrgent }"
      >
        {{ counter }}
      </div>
      <!-- App screen or frontend action: show icon and label -->
      <template v-if="isUiAction(route) || menu.isAppScreen(route)">
        <component :is="itemId" />
        <span class="name">{{ text }}</span>
      </template>
      <!-- Organization homepage -->
      <template v-else-if="menu.isOrganization(route)">
        <HomeIcon />
        <span class="name">Home</span>
      </template>
      <WorkspaceTitle
        v-else-if="menu.isWorkspace(route)"
        :workspace-id="menuWorkspaceId"
      />
      <!-- Database -->
      <DatabaseTitle
        v-else-if="menu.isDatabase(route) && databaseInfo"
        :workspace-id="databaseInfo.workspaceId"
        :table-id="databaseInfo.tableId"
      />
      <!-- Agent -->
      <AgentTitle
        v-else-if="menu.isAgent(route) && agentOrganizationId"
        :agent-id="agentId"
        :organization-id="agentOrganizationId"
      />
      <!-- Object types -->
      <ObjectTitle
        v-else-if="
          (menu.isPage(route) || menu.isObject(route)) && parsedObjectInfo
        "
        :object-id="parsedObjectInfo.rowId"
        :database-id="parsedObjectInfo.tableId"
        :workspace-id="parsedObjectInfo.workspaceId"
      />
      <PageTitle v-else-if="menu.isPage(route)" :page-id="objectId" />
      <TaskTitle v-else-if="menu.isTask(route)" :task-id="objectId" />
      <EventTitle v-else-if="menu.isEvent(route)" :event-id="objectId" />
      <PersonTitle v-else-if="menu.isPerson(route)" :person-id="objectId" />
      <div class="space" />
      <span
        v-if="isMenuExpanded && isBadgeDisplayed && counter"
        class="inline-counter"
        :class="{ urgent: isBadgeUrgent }"
        >{{ counter }}</span
      >
    </a>
  </li>
</template>

<script lang="ts">
import startCase from "lodash/startCase";
import {
  capitalize,
  computed,
  defineComponent,
  PropType,
  ref,
  shallowRef,
  toRefs,
  watch,
} from "vue";
import { useRoute } from "vue-router";

import type {
  AiAgent,
  FullEvent,
  FullPage,
  GeneralizedTask,
  MenuItem,
  People,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import {
  generalActions,
  navigationActions,
} from "@/components/actions/actions";
import EventTitle from "@/components/event/EventTitle.vue";
import ObjectTitle from "@/components/graph/ObjectTitle.vue";
import { useObjectTitle } from "@/components/graph/title";
import HomeIcon from "@/components/icons/HomeIcon.vue";
import * as inject from "@/components/inject";
import AgentTitle from "@/components/menu/AgentTitle.vue";
import DatabaseTitle from "@/components/menu/DatabaseTitle.vue";
import {
  getAgentInfo,
  getDatabaseInfo,
  getItemId,
  getObjectType,
  getOrganizationHomeOrgId,
  getWorkspaceHomeWorkspaceId,
  isUiAction,
  isUiMenuItem,
  ORGANIZATION_APP_SCREEN_LABELS,
  UI_MENU_ITEMS,
  type FrontendMenuItem,
} from "@/components/menu/menuItems";
import PageTitle from "@/components/page/PageTitle.vue";
import PersonTitle from "@/components/person/PersonTitle.vue";
import TaskTitle from "@/components/task/TaskTitle.vue";
import WorkspaceTitle from "@/components/WorkspaceTitle.vue";
import { getOrganization, getWorkspace } from "@/composables/useProfile";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { hotkeys } from "@/utils/hotkeys";
import { keymap } from "@/utils/keymap";

import { TooltipContent, TooltipItem, useTooltip } from "../tooltip";
import { useMenu } from "./controller";

export default defineComponent({
  components: {
    AgentTitle,
    DatabaseTitle,
    EventTitle,
    HomeIcon,
    ObjectTitle,
    WorkspaceTitle,
    PageTitle,
    TaskTitle,
    PersonTitle,
  },
  props: {
    route: { type: Object as PropType<FrontendMenuItem>, required: true },
    // Org context for org-scoped app screens rendered inside an org section.
    orgId: { type: String, required: false },
    selected: { type: Boolean, required: false },
    isBadgeDisplayed: { type: Boolean, default: false },
    counter: { type: Number, required: false },
    isBadgeUrgent: { type: Boolean, default: false },
    stopClickPropagation: { type: Boolean, default: true },
  },
  emits: {
    click: () => true,
  },
  setup(props, { emit }) {
    const { route } = toRefs(props);
    const currentRoute = useRoute();
    const theme = inject.theme();
    const itemEl = ref<HTMLDivElement>();
    const controller = inject.controller();
    const profile = inject.profile();
    const menu = inject.menu();
    const isMenuExpanded = menu.isExpanded;
    const contextMenu = inject.contextMenu();
    const apiMenuItem = computed<MenuItem | undefined>(() =>
      isUiAction(route.value) ? undefined : route.value
    );
    const unpinApiMenuItem = (
      unpin: (item: MenuItem) => Promise<void>
    ): void => {
      const item = apiMenuItem.value;
      if (item) void unpin(item);
    };
    const isTitleTruncated = ref(false);
    const releaser = useReleaser();

    // Get item ID and object details
    const itemId = computed(() => getItemId(route.value));
    const objectId = computed(() => {
      if (route.value.target.kind !== "object") return "";
      return route.value.target.value.id;
    });
    const menuOrgId = computed(() => {
      return getOrganizationHomeOrgId(route.value) || "";
    });
    const menuWorkspaceId = computed(() => {
      return getWorkspaceHomeWorkspaceId(route.value) ?? "";
    });
    const databaseInfo = computed(() => getDatabaseInfo(route.value));
    const agentId = computed(() => getAgentInfo(route.value)?.agentId ?? "");
    const agentOrganizationId = computed(
      () => getAgentInfo(route.value)?.organizationId ?? ""
    );

    const workspaceDatabases = inject.workspaceDatabases();
    const databaseTitle = computed(() => {
      const info = databaseInfo.value;
      if (!info) return "";
      const db = workspaceDatabases.value?.[info.workspaceId]?.[info.tableId];
      return db?.name ? startCase(db.name) : "Database";
    });
    const hasObservedDatabase = ref(false);

    const agentRef = shallowRef<ReleasableRef<AiAgent | null> | null>(null);
    const agentTitle = computed(
      () => agentRef.value?.value?.name ?? agentRef.value?.value?.key ?? ""
    );
    if (
      menu.isAgent(route.value) &&
      agentId.value &&
      agentOrganizationId.value
    ) {
      releaser
        .add(controller.ai.agent.get(agentOrganizationId.value, agentId.value))
        .then((ref: ReleasableRef<AiAgent | null>) => {
          agentRef.value = ref;
        });
    }

    type ParsedObjectInfo = {
      workspaceId: string;
      tableId: string;
      rowId: string;
    };
    const parsedObjectInfo = ref<ParsedObjectInfo | null>(null);
    const parseObjectInfo = async (): Promise<ParsedObjectInfo | null> => {
      try {
        const parsed = await controller.graph.object.id.parse(objectId.value);
        if (!parsed.workspace_id || !parsed.table_id || !parsed.row_id)
          return null;
        const info = {
          workspaceId: parsed.workspace_id,
          tableId: parsed.table_id,
          rowId: parsed.row_id,
        };
        parsedObjectInfo.value = info;
        return info;
      } catch {
        // Native objects do not carry graph workspace information.
        return null;
      }
    };

    const objectWorkspaceId = ref("");
    if (route.value.target.kind === "object") {
      void controller.id
        .workspace(route.value.target.value)
        .then((workspaceId) => {
          objectWorkspaceId.value = workspaceId;
        })
        .catch(() => {
          // Deleted or stale pinned objects are handled by their data watchers.
        });
    }
    const itemWorkspaceId = computed(
      () =>
        databaseInfo.value?.workspaceId ||
        menuWorkspaceId.value ||
        objectWorkspaceId.value
    );
    const tooltipWorkspace = computed(() => {
      if (!itemWorkspaceId.value) return undefined;
      const workspace = getWorkspace(
        profile.value,
        itemWorkspaceId.value
      )?.workspace;
      return workspace && !workspace.personal ? workspace.name : undefined;
    });

    // Keep reactive references to the controller data
    const pageRef = shallowRef<ReleasableRef<FullPage | null> | null>(null);
    const taskRef = shallowRef<ReleasableRef<GeneralizedTask | null> | null>(
      null
    );
    const eventRef = shallowRef<ReleasableRef<FullEvent | null> | null>(null);
    const personRef = shallowRef<ReleasableRef<People | null> | null>(null);
    const objectRef = shallowRef<ReleasableRef<string | null> | null>(null);
    // Organization / workspace pins resolve from the profile's live
    // organization tree; `null` when the id is gone from the profile.
    const menuOrganization = computed(() =>
      menu.isOrganization(route.value)
        ? (getOrganization(profile.value, menuOrgId.value) ?? null)
        : null
    );
    const menuWorkspace = computed(() =>
      menu.isWorkspace(route.value) && menuWorkspaceId.value
        ? (getWorkspace(profile.value, menuWorkspaceId.value)?.workspace ??
          null)
        : null
    );

    const { goTo, isRoute } = useMenu(menu, ref(route.value), props.orgId);
    const text = computed(() => {
      if (route.value.target.kind === "app_screen") {
        const organizationLabel =
          ORGANIZATION_APP_SCREEN_LABELS[route.value.target.value];
        if (organizationLabel) return organizationLabel;
      }
      return itemId.value ? capitalize(itemId.value) : "";
    });

    const loadObjectTitle = async (parsed: ParsedObjectInfo) => {
      const objectIdFull = await controller.graph.object.id.make({
        workspace_id: parsed.workspaceId,
        table_id: parsed.tableId,
        row_id: parsed.rowId,
      });
      objectRef.value = await releaser.add(
        useObjectTitle(controller, objectIdFull)
      );
    };

    // Load and keep reactive references
    if (menu.isPage(route.value)) {
      parseObjectInfo().then((parsed) => {
        if (parsed) {
          void loadObjectTitle(parsed);
        } else {
          releaser.add(controller.page.get(objectId.value)).then((page) => {
            pageRef.value = page;
          });
        }
      });
    } else if (menu.isTask(route.value)) {
      releaser.add(controller.task.get(objectId.value)).then((task) => {
        taskRef.value = task;
      });
    } else if (menu.isEvent(route.value)) {
      releaser.add(controller.event.get(objectId.value)).then((event) => {
        eventRef.value = event;
      });
    } else if (menu.isPerson(route.value)) {
      releaser.add(controller.people.get(objectId.value)).then((people) => {
        personRef.value = people;
      });
    } else if (menu.isObject(route.value)) {
      // For generic objects, parse the ID and load the title
      parseObjectInfo().then((parsed) => {
        if (parsed) {
          void loadObjectTitle(parsed);
        }
      });
    }

    // Create computed properties for the titles
    const pageTitle = computed(() => {
      return pageRef.value?.value?.title || "Untitled";
    });

    const taskTitle = computed(() => {
      return taskRef.value?.value?.title || "Untitled";
    });

    const eventTitle = computed(() => {
      return eventRef.value?.value?.title || "Untitled";
    });

    const personTitle = computed(() => {
      return (
        personRef.value?.value?.name?.display ||
        personRef.value?.value?.email ||
        "Untitled"
      );
    });

    const objectTitle = computed(() => objectRef.value?.value || "Untitled");

    const organizationTitle = computed(() => {
      return menuOrganization.value?.name || "Organization";
    });

    const workspaceTitle = computed(() => {
      return menuWorkspace.value?.name || "Workspace";
    });

    // Watch for deleted objects and automatically unpin them from the menu
    if (menu.isPage(route.value)) {
      watch(
        () => pageRef.value?.value,
        (pageValue) => {
          if (pageRef.value && pageValue === null) {
            unpinApiMenuItem(menu.unpinObject);
          }
        }
      );
      watch(
        () => objectRef.value?.value,
        (objectValue) => {
          if (
            objectRef.value &&
            objectValue !== undefined &&
            objectValue === null
          ) {
            unpinApiMenuItem(menu.unpinObject);
          }
        }
      );
    } else if (menu.isTask(route.value)) {
      watch(
        () => taskRef.value?.value,
        (taskValue) => {
          if (taskRef.value && taskValue === null) {
            unpinApiMenuItem(menu.unpinObject);
          }
        }
      );
    } else if (menu.isEvent(route.value)) {
      watch(
        () => eventRef.value?.value,
        (eventValue) => {
          if (eventRef.value && eventValue === null) {
            unpinApiMenuItem(menu.unpinObject);
          }
        }
      );
    } else if (menu.isPerson(route.value)) {
      watch(
        () => personRef.value?.value,
        (personValue) => {
          if (personRef.value && personValue === null) {
            unpinApiMenuItem(menu.unpinObject);
          }
        }
      );
    } else if (menu.isObject(route.value)) {
      watch(
        () => objectRef.value?.value,
        (objectValue) => {
          if (objectRef.value && objectValue === null) {
            unpinApiMenuItem(menu.unpinObject);
          }
        }
      );
    } else if (menu.isDatabase(route.value)) {
      watch(
        [() => workspaceDatabases.value, databaseInfo],
        () => {
          const info = databaseInfo.value;
          if (!info) return;
          if (!workspaceDatabases.value) return;
          const db = workspaceDatabases.value[info.workspaceId]?.[info.tableId];
          if (db) {
            hasObservedDatabase.value = true;
          } else if (hasObservedDatabase.value) {
            // Menu items and databases are separate reactive refs; a newly
            // pinned database can appear in the menu before the database list
            // catches up, so only treat absence as deletion after observing it.
            unpinApiMenuItem(menu.unpinDatabase);
          }
        },
        { immediate: true, deep: true }
      );
    } else if (menu.isAgent(route.value)) {
      watch(
        () => agentRef.value?.value,
        (agentValue) => {
          if (agentRef.value && agentValue === null) {
            unpinApiMenuItem(menu.unpinAgent);
          }
        }
      );
    } else if (menu.isOrganization(route.value)) {
      watch(
        menuOrganization,
        (org) => {
          if (org === null) {
            unpinApiMenuItem(menu.unpinObject);
          }
        },
        { immediate: true }
      );
    } else if (menu.isWorkspace(route.value)) {
      watch(
        menuWorkspace,
        (ws) => {
          if (ws === null) {
            unpinApiMenuItem(menu.unpinObject);
          }
        },
        { immediate: true }
      );
    }

    const itemTooltip = (
      tooltipText?: string,
      shortcut?: string
    ): TooltipContent =>
      ({
        text: tooltipText,
        shortcut,
        detail: tooltipWorkspace.value,
      }) satisfies TooltipItem;
    const tooltipContent = computed<TooltipContent>(() => {
      if (
        isMenuExpanded.value &&
        !isTitleTruncated.value &&
        !tooltipWorkspace.value
      )
        return undefined;
      // UI-only items (using type-safe constants)
      if (itemId.value === UI_MENU_ITEMS.Collapse) {
        return itemTooltip(
          isMenuExpanded.value ? "Collapse" : "Expand",
          generalActions.collapse.shortcutsDisplay
        );
      } else if (itemId.value === UI_MENU_ITEMS.Search) {
        return itemTooltip(
          navigationActions.search.text,
          navigationActions.search.shortcutsDisplay
        );
      } else if (itemId.value === UI_MENU_ITEMS.Settings) {
        return itemTooltip(
          generalActions.settings.text,
          generalActions.settings.shortcutsDisplay
        );
      } else if (itemId.value === UI_MENU_ITEMS.Help) {
        return itemTooltip(generalActions.help.text);
      }
      // Screens & pinned objects
      const indexToShortcutDisplay = (index: number) => {
        if (index < 0 || index >= 9) return undefined;
        return (
          hotkeys.format.stringify(
            [keymap.keyMod, `${parseInt(keymap.key1, 10) + index}`],
            keymap.formatSymbol,
            keymap.separatorSpace
          ) || undefined
        );
      };
      const index = apiMenuItem.value
        ? menu.findQuickAccessMenuItemIndex(apiMenuItem.value, props.orgId)
        : -1;
      const shortcutsDisplay = indexToShortcutDisplay(index);
      if (menu.isPage(route.value)) {
        return itemTooltip(
          parsedObjectInfo.value ? objectTitle.value : pageTitle.value,
          shortcutsDisplay
        );
      } else if (menu.isTask(route.value)) {
        return itemTooltip(taskTitle.value || "Loading...", shortcutsDisplay);
      } else if (menu.isEvent(route.value)) {
        return itemTooltip(eventTitle.value || "Loading...", shortcutsDisplay);
      } else if (menu.isPerson(route.value)) {
        return itemTooltip(personTitle.value || "Loading...", shortcutsDisplay);
      } else if (menu.isObject(route.value)) {
        return itemTooltip(objectTitle.value || "Loading...", shortcutsDisplay);
      } else if (menu.isOrganization(route.value)) {
        return itemTooltip(
          organizationTitle.value || "Loading...",
          shortcutsDisplay
        );
      } else if (menu.isWorkspace(route.value)) {
        return itemTooltip(
          workspaceTitle.value || "Loading...",
          shortcutsDisplay
        );
      } else if (menu.isDatabase(route.value)) {
        return itemTooltip(
          databaseTitle.value || "Loading...",
          shortcutsDisplay
        );
      } else if (menu.isAgent(route.value)) {
        return itemTooltip(agentTitle.value || "Loading...", shortcutsDisplay);
      }
      // Fallback to the item's title
      return itemTooltip(text.value, shortcutsDisplay);
    });

    const isPageSelected = () => {
      if (!menu.isPage(route.value)) return false;
      if (parsedObjectInfo.value) {
        return (
          currentRoute.name === RouteNames.Object &&
          currentRoute.params.rowId === parsedObjectInfo.value.rowId &&
          currentRoute.params.tableId === parsedObjectInfo.value.tableId &&
          currentRoute.params.workspaceId === parsedObjectInfo.value.workspaceId
        );
      }
      return (
        currentRoute.name === RouteNames.OrgPage &&
        currentRoute.params.pageId === objectId.value &&
        (!props.orgId || currentRoute.params.orgId === props.orgId)
      );
    };
    const isTaskSelected = () => {
      return (
        currentRoute.name === RouteNames.OrgTask &&
        currentRoute.params.taskId === objectId.value &&
        (!props.orgId || currentRoute.params.orgId === props.orgId)
      );
    };
    const isEventSelected = () => {
      return (
        currentRoute.name === RouteNames.Event &&
        currentRoute.params.id === objectId.value
      );
    };
    const isPersonSelected = () => {
      return (
        currentRoute.name === RouteNames.Person &&
        currentRoute.params.id === objectId.value
      );
    };
    const isObjectSelected = () => {
      if (!menu.isObject(route.value) || !parsedObjectInfo.value) return false;
      // A pinned canvas opens as the canvas screen (see navigateToMenuItem),
      // whose route carries no table id — match on workspace and row.
      if (currentRoute.name === RouteNames.Canvas) {
        return (
          currentRoute.params.rowId === parsedObjectInfo.value.rowId &&
          currentRoute.params.workspaceId === parsedObjectInfo.value.workspaceId
        );
      }
      return (
        currentRoute.name === RouteNames.Object &&
        currentRoute.params.rowId === parsedObjectInfo.value.rowId &&
        currentRoute.params.tableId === parsedObjectInfo.value.tableId &&
        currentRoute.params.workspaceId === parsedObjectInfo.value.workspaceId
      );
    };
    const isOrganizationSelected = () => {
      if (!menu.isOrganization(route.value)) return false;
      return (
        currentRoute.name === RouteNames.Organization &&
        currentRoute.params.orgId === menuOrgId.value
      );
    };
    const isWorkspaceSelected = () => {
      if (!menu.isWorkspace(route.value)) return false;
      const wsId = getWorkspaceHomeWorkspaceId(route.value);
      if (!wsId) return false;
      return (
        currentRoute.name === RouteNames.Workspace &&
        currentRoute.params.workspaceId === wsId
      );
    };
    const isDatabaseSelected = () => {
      if (!menu.isDatabase(route.value) || !databaseInfo.value) return false;
      return (
        currentRoute.name === RouteNames.Database &&
        currentRoute.params.workspaceId === databaseInfo.value.workspaceId &&
        currentRoute.params.databaseId === databaseInfo.value.tableId
      );
    };
    const isAgentSelected = () => {
      if (!menu.isAgent(route.value)) return false;
      const info = getAgentInfo(route.value);
      return (
        info &&
        currentRoute.name === RouteNames.Agent &&
        currentRoute.params.agentId === info.agentId
      );
    };

    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(itemEl, tooltipContent, "right");
    releaser.defer(releaseTooltip);

    const updateIsTitleTruncated = () => {
      if (!isMenuExpanded.value) {
        isTitleTruncated.value = true;
        return;
      }
      const el = itemEl.value;
      const nameEl = el?.querySelector(".name");
      if (!(nameEl instanceof HTMLElement)) {
        isTitleTruncated.value = false;
        return;
      }
      // Avoid sub-pixel false positives.
      isTitleTruncated.value = nameEl.scrollWidth > nameEl.clientWidth + 1;
    };

    const onMouseEnter = () => {
      updateIsTitleTruncated();
      showTooltip();
    };
    const onMouseLeave = () => {
      hideTooltip();
    };

    const onClick = (event: MouseEvent) => {
      hideTooltip();
      if (props.stopClickPropagation) {
        event.stopPropagation();
      }
      goTo(() => {
        emit("click");
      }, event);
    };

    const showContextMenu = (e: MouseEvent) => {
      hideTooltip();

      if (isUiMenuItem(itemId.value)) return;
      const item = apiMenuItem.value;
      if (!item) return;

      if (menu.isTask(item) && taskRef.value?.value) {
        contextMenu.value.show({ type: "task", value: taskRef.value.value }, e);
      } else if (menu.isEvent(item) && eventRef.value?.value) {
        const ev = eventRef.value.value;
        contextMenu.value.show(
          {
            type: "event",
            value: {
              ...ev,
              isCancellable: ev.editable,
              isDeletable: ev.editable && !ev.draft,
            },
          },
          e
        );
      } else if (
        (menu.isPage(item) || menu.isObject(item)) &&
        parsedObjectInfo.value
      ) {
        contextMenu.value.show(
          {
            type: "object",
            value: {
              type: "object",
              workspace_id: parsedObjectInfo.value.workspaceId,
              table_id: parsedObjectInfo.value.tableId,
              row_id: parsedObjectInfo.value.rowId,
            },
          },
          e
        );
      } else if (menu.isPage(item) && pageRef.value?.value) {
        contextMenu.value.show({ type: "page", value: pageRef.value.value }, e);
      } else if (menu.isPerson(item) && personRef.value?.value) {
        contextMenu.value.show(
          { type: "people", value: personRef.value.value },
          e
        );
      } else if (
        menu.isAppScreen(item) ||
        menu.isOrganization(item) ||
        menu.isWorkspace(item) ||
        menu.isDatabase(item) ||
        menu.isAgent(item)
      ) {
        contextMenu.value.show({ type: "menu-item", value: item }, e);
      }
    };

    return {
      databaseInfo,
      agentId,
      agentOrganizationId,
      getObjectType,
      goTo,
      onMouseEnter,
      onMouseLeave,
      isMenuExpanded,
      isRoute,
      itemEl,
      itemId,
      isUiAction,
      objectId,
      menuOrgId,
      menuWorkspaceId,
      parsedObjectInfo,
      onClick,
      showContextMenu,
      text,
      theme,
      menu,
      isPageSelected,
      isTaskSelected,
      isEventSelected,
      isPersonSelected,
      isObjectSelected,
      isOrganizationSelected,
      isWorkspaceSelected,
      isDatabaseSelected,
      isAgentSelected,
    };
  },
});
</script>

<style scoped lang="scss">
li.menu-item {
  .menu-name {
    position: relative;
    display: flex;
    align-items: center;
  }

  // Hover effect for task checkbox (matches svg hover behavior in menu.scss)
  &:hover :deep(.task-checkbox > div),
  &.selected :deep(.task-checkbox > div) {
    @include themed() {
      box-shadow: t($icon);
      background: t($bg-icon-menu-hover);
    }
  }

  &:active :deep(.task-checkbox > div) {
    @include themed() {
      box-shadow: t($icon-active);
      background: t($bg-icon-menu-active);
    }
  }

  // When menu is expanded, remove the icon hover effect (background is on the li instead)
  &.isMenuExpanded:hover :deep(.task-checkbox > div),
  &.isMenuExpanded.selected :deep(.task-checkbox > div),
  &.isMenuExpanded:active :deep(.task-checkbox > div) {
    @include themed() {
      box-shadow: none;
      background: transparent;
    }
  }

  // Hover effect for object emoji icons (collapsed mode)
  &:not(.isMenuExpanded):hover :deep(.emoji),
  &:not(.isMenuExpanded):hover :deep(.default-icon),
  &:not(.isMenuExpanded).selected :deep(.emoji),
  &:not(.isMenuExpanded).selected :deep(.default-icon) {
    @include themed() {
      box-shadow: t($icon);
      background: t($bg-icon-menu-hover);
    }
  }

  &:not(.isMenuExpanded):active :deep(.emoji),
  &:not(.isMenuExpanded):active :deep(.default-icon) {
    @include themed() {
      box-shadow: t($icon-active);
      background: t($bg-icon-menu-active);
    }
  }
  :deep(.default-icon) {
    font-size: 16px;
    padding-inline-start: 1px;
  }
  .badge {
    height: 8px;
    width: 8px;
    top: 5px;
    inset-inline-end: 5px;
    position: absolute;
    border-radius: 50px;
    @include themed() {
      background-color: t($secondary);
    }
  }
  .icon &.badge {
    height: 8px;
    width: 8px;
    top: 5px;
    inset-inline-start: 19px;
  }
  .badge.counter,
  .inline-counter {
    border-radius: 20px;
    @include themed() {
      background-color: t($grey-10);
      color: t($grey-50);
    }
  }
  .badge.counter.urgent,
  .inline-counter.urgent {
    @include themed() {
      background-color: t($secondary-10);
      color: t($secondary-50);
    }
  }
  .badge.counter {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: auto;
    height: auto;
    min-width: 18px;
    font-size: 10px;
    line-height: 10px;
    padding: 2px 4px;
    top: -5px;
    inset-inline-end: -3px;
    @include themed() {
      border: 2px solid t($bg);
    }
  }
  .space {
    width: inherit;
  }
  .inline-counter {
    @extend %font-small-regular;
    box-sizing: border-box;
    flex-shrink: 0;
    padding: 2px 6px;
    min-width: 21px;
    text-align: center;
  }

  // Flip the collapse arrow when menu is collapsed (not expanded)
  &.is-collapse:not(.isMenuExpanded) svg {
    transform: scaleX(-1);
  }
}
</style>
